<template><!-- toggle via F key; no visible button --></template>

<script setup>
import { Pane } from 'tweakpane'
import { FONT_OPTIONS, loadFontPaneState, saveFontPaneState, applyDesignVars } from '@/composables/useFontPane'

const state = reactive(loadFontPaneState())
applyDesignVars(state)

let pane

const persist = () => saveFontPaneState({ ...state })

const setHidden = (value) => {
  state.hidden = value
  if (pane) pane.hidden = value
  persist()
}

const toggle = () => setHidden(!state.hidden)

const onKeyDown = (e) => {
  if (e.key === 'f' || e.key === 'F') {
    // Don't fire when typing in an input/textarea
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)) return
    toggle()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeyDown)

  pane = new Pane({ title: 'Fonts' })
  pane.hidden = state.hidden

  const familyBinding = pane.addBinding(state, 'family', {
    label: 'Font',
    options: Object.fromEntries(Object.keys(FONT_OPTIONS).map((name) => [name, name])),
  })

  const sizeBinding = pane.addBinding(state, 'fontSize', { label: 'Size', min: 8, max: 32, step: 1 })

  const mediumBinding = pane.addBinding(state, 'medium', { label: 'Medium weight' })
  mediumBinding.disabled = !FONT_OPTIONS[state.family].hasMedium

  const italicBinding = pane.addBinding(state, 'italic', { label: 'Italic' })

  const marginBinding = pane.addBinding(state, 'pageMargin', { label: 'Margin', min: 0, max: 96, step: 4 })

  familyBinding.on('change', () => {
    if (!FONT_OPTIONS[state.family].hasMedium) state.medium = false
    mediumBinding.disabled = !FONT_OPTIONS[state.family].hasMedium
    applyDesignVars(state)
    persist()
  })

  sizeBinding.on('change', () => {
    applyDesignVars(state)
    persist()
  })

  mediumBinding.on('change', () => {
    applyDesignVars(state)
    persist()
  })

  italicBinding.on('change', () => {
    applyDesignVars(state)
    persist()
  })

  marginBinding.on('change', () => {
    state.pageMarginTouched = true
    applyDesignVars(state)
    persist()
  })

  pane.addButton({ title: 'Hide panel' }).on('click', () => setHidden(true))
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeyDown)
  pane?.dispose()
})
</script>

<style>
/* Tweakpane's own container sits at z-index:auto, so the fixed, full-bleed
   .stage (position:fixed, empty <ul> included) would otherwise intercept
   clicks meant for the panel even though it paints nothing visible over it. */
.tp-dfwv {
  z-index: 1000;
}

.fontpane-toggle {
  position: fixed;
  right: var(--page-margin, 32px);
  bottom: var(--page-margin, 28px);
  z-index: 20;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
}


@media only screen and (max-width: 700px) {
  .fontpane-toggle {
    right: var(--page-margin, 16px);
    bottom: var(--page-margin, 16px);
  }
}
</style>
